/**
 * Timeline Duration Helper
 *
 * Automatically calculates required timeline duration based on all items and their
 * entrance/exit animations to ensure no content gets clipped during rendering.
 */

// Debug logging control
const DEBUG_TIMELINE_DURATION = false;

import { TimelineImpl } from '../Timeline';
import { LayerItem } from '../types/base';
import { AnimatedItem } from '../types/animated';

export interface DurationCalculationResult {
    calculatedDuration: number;
    originalDuration: number;
    needsExtension: boolean;
    itemBreakdown: ItemDurationInfo[];
    animationBuffers: {
        totalEntranceBuffer: number;
        totalExitBuffer: number;
    };
}

export interface ItemDurationInfo {
    itemId: string;
    layerType: string;
    scheduledStart: number;
    scheduledEnd: number;
    actualStart: number;      // Including entrance animation buffer
    actualEnd: number;        // Including exit animation buffer  
    entranceBuffer: number;   // Duration before scheduled start
    exitBuffer: number;       // Duration after scheduled end
    hasAnimations: boolean;
}

/**
 * Timeline Duration Calculator
 */
export class TimelineDurationCalculator {
    
    /**
     * Calculate the minimum required timeline duration to accommodate all items and animations
     */
    static calculateRequiredDuration(timeline: TimelineImpl): DurationCalculationResult {
        const originalDuration = timeline.durationInSeconds || 0;
        let maxEndTime = 0;
        let maxEntranceBuffer = 0;
        let maxExitBuffer = 0;
        const itemBreakdown: ItemDurationInfo[] = [];

        // Check all layers for their actual timing requirements
        const allLayers = [
            { items: timeline.backgroundLayer.items, type: 'background' },
            { items: timeline.sceneLayer.items, type: 'scene' },
            { items: timeline.overlayLayer.items, type: 'overlay' },
            { items: timeline.overlayTransitionLayer?.items || [], type: 'overlayTransition' },
            { items: timeline.textOverlayLayer.items, type: 'textOverlay' },
            { items: timeline.captionLayer.items, type: 'caption' },
            { items: timeline.brandingLayer.items, type: 'branding' }
        ];

        allLayers.forEach(layer => {
            layer.items.forEach(item => {
                const analysis = this.analyzeItemDuration(item, layer.type);
                itemBreakdown.push(analysis);
                
                maxEndTime = Math.max(maxEndTime, analysis.actualEnd);
                maxEntranceBuffer = Math.max(maxEntranceBuffer, analysis.entranceBuffer);
                maxExitBuffer = Math.max(maxExitBuffer, analysis.exitBuffer);
            });
        });

        const calculatedDuration = Math.max(maxEndTime, originalDuration);

        // Add safety buffer for complex animations
        const safetyBuffer = 0.1; // 100ms safety margin
        const finalDuration = calculatedDuration + safetyBuffer;
        
        const needsExtension = finalDuration > originalDuration;

        return {
            calculatedDuration: finalDuration,
            originalDuration,
            needsExtension,
            itemBreakdown,
            animationBuffers: {
                totalEntranceBuffer: maxEntranceBuffer,
                totalExitBuffer: maxExitBuffer
            }
        };
    }

    /**
     * Analyze individual item duration requirements including animations
     */
    private static analyzeItemDuration(item: LayerItem, layerType: string): ItemDurationInfo {
        const scheduledStart = item.startAt;
        const scheduledEnd = item.startAt + (item.duration || 0);
        
        let entranceBuffer = 0;
        let exitBuffer = 0;
        let hasAnimations = false;

        // Check if item has animation capabilities
        if (this.isAnimatedItem(item)) {
            const animatedItem = item as AnimatedItem;
            
            // Calculate entrance animation buffer
            if (animatedItem.entranceAnimation) {
                entranceBuffer = animatedItem.entranceAnimation.timing.duration;
                hasAnimations = true;
            }
            
            // Calculate exit animation buffer
            if (animatedItem.exitAnimation) {
                exitBuffer = animatedItem.exitAnimation.timing.duration;
                hasAnimations = true;
            }
        }

        const actualStart = scheduledStart - entranceBuffer;
        const actualEnd = scheduledEnd + exitBuffer;

        return {
            itemId: item.id,
            layerType,
            scheduledStart,
            scheduledEnd,
            actualStart,
            actualEnd,
            entranceBuffer,
            exitBuffer,
            hasAnimations
        };
    }

    /**
     * Check if item has animation capabilities
     */
    private static isAnimatedItem(item: LayerItem): item is AnimatedItem {
        return 'entranceAnimation' in item || 'exitAnimation' in item;
    }

    /**
     * Update timeline duration if needed
     */
    static updateTimelineDuration(timeline: TimelineImpl): boolean {
        const calculation = this.calculateRequiredDuration(timeline);
        
        if (calculation.needsExtension) {
            const oldDuration = timeline.durationInSeconds || 0;
            timeline.durationInSeconds = calculation.calculatedDuration;
            
            if (DEBUG_TIMELINE_DURATION) console.log(`[TimelineDuration] Extended timeline duration: ${oldDuration.toFixed(2)}s → ${calculation.calculatedDuration.toFixed(2)}s`);
            if (DEBUG_TIMELINE_DURATION) console.log(`[TimelineDuration] Animation buffers: entrance ${calculation.animationBuffers.totalEntranceBuffer.toFixed(2)}s, exit ${calculation.animationBuffers.totalExitBuffer.toFixed(2)}s`);
            
            return true;
        }
        
        return false;
    }

    /**
     * Generate detailed duration report for debugging
     */
    static generateDurationReport(timeline: TimelineImpl): string {
        const calculation = this.calculateRequiredDuration(timeline);
        
        let report = '# Timeline Duration Analysis\n\n';
        report += `**Original Duration:** ${calculation.originalDuration.toFixed(2)}s\n`;
        report += `**Calculated Duration:** ${calculation.calculatedDuration.toFixed(2)}s\n`;
        report += `**Extension Needed:** ${calculation.needsExtension ? 'Yes' : 'No'}\n\n`;
        
        if (calculation.animationBuffers.totalEntranceBuffer > 0 || calculation.animationBuffers.totalExitBuffer > 0) {
            report += '## Animation Buffers\n';
            report += `- **Max Entrance Buffer:** ${calculation.animationBuffers.totalEntranceBuffer.toFixed(2)}s\n`;
            report += `- **Max Exit Buffer:** ${calculation.animationBuffers.totalExitBuffer.toFixed(2)}s\n\n`;
        }
        
        report += '## Item Duration Breakdown\n\n';
        
        // Group by layer type
        const layerGroups: { [key: string]: ItemDurationInfo[] } = {};
        calculation.itemBreakdown.forEach(item => {
            if (!layerGroups[item.layerType]) {
                layerGroups[item.layerType] = [];
            }
            layerGroups[item.layerType].push(item);
        });
        
        Object.keys(layerGroups).forEach(layerType => {
            const items = layerGroups[layerType];
            if (items.length > 0) {
                report += `### ${layerType.charAt(0).toUpperCase() + layerType.slice(1)} Layer\n\n`;
                
                items.forEach(item => {
                    report += `**${item.itemId}**:\n`;
                    report += `- Scheduled: ${item.scheduledStart.toFixed(2)}s - ${item.scheduledEnd.toFixed(2)}s\n`;
                    
                    if (item.hasAnimations) {
                        report += `- Actual: ${item.actualStart.toFixed(2)}s - ${item.actualEnd.toFixed(2)}s\n`;
                        if (item.entranceBuffer > 0) {
                            report += `- Entrance Buffer: ${item.entranceBuffer.toFixed(2)}s\n`;
                        }
                        if (item.exitBuffer > 0) {
                            report += `- Exit Buffer: ${item.exitBuffer.toFixed(2)}s\n`;
                        }
                    }
                    report += '\n';
                });
            }
        });
        
        return report;
    }

    /**
     * Validate timeline has sufficient duration for all animations
     */
    static validateTimelineDuration(timeline: TimelineImpl): {
        isValid: boolean;
        issues: string[];
        recommendations: string[];
    } {
        const calculation = this.calculateRequiredDuration(timeline);
        const issues: string[] = [];
        const recommendations: string[] = [];

        if (calculation.needsExtension) {
            issues.push(`Timeline duration (${calculation.originalDuration.toFixed(2)}s) is insufficient for all animations`);
            issues.push(`Required duration: ${calculation.calculatedDuration.toFixed(2)}s`);
            recommendations.push('Use TimelineDurationCalculator.updateTimelineDuration() to auto-extend');
        }

        // Check for items that might be clipped
        calculation.itemBreakdown.forEach(item => {
            if (item.actualEnd > calculation.originalDuration) {
                issues.push(`Item '${item.itemId}' extends beyond timeline (ends at ${item.actualEnd.toFixed(2)}s)`);
            }
            if (item.actualStart < 0) {
                issues.push(`Item '${item.itemId}' entrance animation starts before timeline (${item.actualStart.toFixed(2)}s)`);
                recommendations.push('Consider adjusting item start time or entrance animation timing');
            }
        });

        return {
            isValid: issues.length === 0,
            issues,
            recommendations
        };
    }
}

/**
 * Convenience function for automatic timeline duration calculation
 */
export function ensureTimelineDuration(timeline: TimelineImpl): DurationCalculationResult {
    const result = TimelineDurationCalculator.calculateRequiredDuration(timeline);
    
    if (result.needsExtension) {
        timeline.durationInSeconds = result.calculatedDuration;
        if (DEBUG_TIMELINE_DURATION) console.log(`[TimelineDuration] Auto-extended timeline to ${result.calculatedDuration.toFixed(2)}s for animations`);
    }
    
    return result;
}

/**
 * Debug utility to log timeline duration analysis
 */
export function debugTimelineDuration(timeline: TimelineImpl): void {
    const report = TimelineDurationCalculator.generateDurationReport(timeline);
    if (DEBUG_TIMELINE_DURATION) console.log(report);
    
    const validation = TimelineDurationCalculator.validateTimelineDuration(timeline);
    if (!validation.isValid) {
        console.warn('[TimelineDuration] Validation Issues:', validation.issues);
        console.warn('[TimelineDuration] Recommendations:', validation.recommendations);
    }
}